import React, { useState, useEffect, useCallback } from 'react';
import authedFetch from '../../lib/authedFetch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ArrowRight, Database, GitBranch, ShieldCheck, Info, FileDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROV_META = {
  measured: { label: 'Measured', c: '#059669', bg: 'bg-emerald-50', tx: 'text-emerald-700' },
  modeled: { label: 'Modeled', c: '#d97706', bg: 'bg-amber-50', tx: 'text-amber-700' },
  none: { label: 'No data', c: '#94a3b8', bg: 'bg-slate-50', tx: 'text-slate-600' },
};

const confTone = (conf) => (conf >= 0.75 ? 'text-emerald-600' : conf >= 0.5 ? 'text-amber-600' : 'text-rose-600');

const SOURCE_META = {
  raw: { label: 'Raw components', cls: 'bg-emerald-50 text-emerald-700' },
  partial: { label: 'Partial (some fallback)', cls: 'bg-amber-50 text-amber-700' },
  fallback: { label: 'Fallback (0-100 value)', cls: 'bg-rose-50 text-rose-700' },
  modeled: { label: 'Modeled', cls: 'bg-amber-50 text-amber-700' },
  none: { label: 'No data', cls: 'bg-slate-50 text-slate-500' },
};

const FactorBar = ({ label, value, highlight }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  return (
    <div className="flex items-center gap-2" data-testid={`audit-factor-${label}`}>
      <span className="text-xs text-slate-500 w-28 capitalize">{label.replace(/_/g, ' ')}</span>
      <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? '#1f2a44' : '#f1f5f9' }}>
        <div className="h-full" style={{ width: `${Math.round((value || 0) * 100)}%`, backgroundColor: highlight ? '#10b981' : '#06b6d4' }} />
      </div>
      <span className="text-xs text-slate-600 w-8 text-right">{Math.round((value || 0) * 100)}</span>
    </div>
  );
};

/**
 * Slide-over audit trail: traces a single LEI measure back to the raw LMS inputs,
 * shows the formula per sub-measure, per-cohort breakdown, and the confidence band.
 */
export const MeasureAuditDrawer = ({ open, onOpenChange, measureId, organizationId }) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!measureId) return;
    setLoading(true);
    setData(null);
    try {
      const params = new URLSearchParams({ measure_id: measureId });
      if (organizationId) params.set('organization_id', organizationId);
      const r = await authedFetch(`${API_URL}/api/lei-raw/measure-trace?${params}`, {});
      setData(await r.json());
    } catch (e) {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  }, [measureId, organizationId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const [exporting, setExporting] = useState(false);
  const downloadPdf = async () => {
    if (!measureId) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ measure_id: measureId });
      if (organizationId) params.set('organization_id', organizationId);
      const r = await authedFetch(`${API_URL}/api/lei-raw/measure-trace/pdf?${params}`, {});
      if (!r.ok) throw new Error('pdf');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `audit_${measureId}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit trail PDF downloaded');
    } catch (e) {
      toast.error('Could not generate PDF');
    } finally {
      setExporting(false);
    }
  };

  const prov = PROV_META[data?.provenance] || PROV_META.none;
  const conf = data?.confidence;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className={cn('w-full sm:max-w-lg overflow-y-auto', isDark && 'home-dark-scope bg-[#0b1220] text-slate-200 border-l border-[#1f2a44]')} data-testid="measure-audit-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-base">
            <GitBranch className="h-5 w-5 text-cyan-600" />
            {data?.name || 'Measure audit trail'}
          </SheetTitle>
          <SheetDescription>Where this number came from — traced from the raw LMS inputs.</SheetDescription>
        </SheetHeader>

        {!loading && data && !data.error && data.raw_available && (
          <Button size="sm" variant="outline" onClick={downloadPdf} disabled={exporting}
            className="mt-3 gap-2" data-testid="audit-download-pdf-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            Download audit PDF
          </Button>
        )}

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}

        {!loading && data && !data.error && (
          <div className="mt-4 space-y-5">
            {/* Score + provenance */}
            <div className="flex items-center justify-between rounded-xl border p-4">
              <div>
                <div className="text-xs text-slate-500">Computed index</div>
                <div className="text-3xl font-bold text-slate-800" data-testid="audit-index">
                  {data.index != null ? Number(data.index).toFixed(1) : '--'}
                </div>
                <div className="text-xs text-slate-400">Target {data.target} · Benchmark {data.benchmark}</div>
              </div>
              <Badge className={cn('border-0 gap-1', prov.bg, prov.tx)} data-testid="audit-provenance-badge">
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: prov.c }} />
                {prov.label}
              </Badge>
            </div>

            {/* Modeled / no raw */}
            {!data.raw_available && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex gap-2" data-testid="audit-no-raw">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{data.note}</span>
              </div>
            )}

            {/* Confidence band */}
            {conf && (
              <div className="rounded-xl border p-4 space-y-2" data-testid="audit-confidence">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <ShieldCheck className="h-4 w-4 text-cyan-600" /> Confidence
                  </span>
                  <span className={cn('text-sm font-semibold', confTone(conf.confidence))}>
                    {Math.round(conf.confidence * 100)}% · ±{conf.band} pts
                  </span>
                </div>
                {conf.source_mode && SOURCE_META[conf.source_mode] && (
                  <div className="flex items-center gap-2 text-[11px]" data-testid="audit-source-mode">
                    <span className={cn('rounded-full px-2 py-0.5 font-medium', SOURCE_META[conf.source_mode].cls)}>
                      {SOURCE_META[conf.source_mode].label}
                    </span>
                    {conf.subs_total > 0 && (
                      <span className="text-slate-400">{conf.components_used}/{conf.subs_total} sub-measures from raw components</span>
                    )}
                  </div>
                )}
                <div className="space-y-1.5 pt-1">
                  <FactorBar label="input_completeness" value={conf.factors.component_integrity} highlight />
                  <FactorBar label="recency" value={conf.factors.recency} />
                  <FactorBar label="sample_size" value={conf.factors.sample_size} />
                  <FactorBar label="coverage" value={conf.factors.completeness} />
                </div>
                {conf.source_mode !== 'raw' && conf.subs_total > 0 && (
                  <p className="text-[11px] text-amber-700 pt-0.5" data-testid="audit-integrity-nudge">
                    Capture the raw components below (instead of a 0-100 value) to raise this measure's confidence.
                  </p>
                )}
              </div>
            )}

            {/* Formula / sub-results */}
            {data.raw_available && (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">How it's calculated</div>
                {data.sub_results.map((s, i) => (
                  <div key={i} className="rounded-lg border p-3" data-testid={`audit-subresult-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <code className="text-xs text-slate-600">{s.formula}</code>
                      <span className="flex items-center gap-1.5 shrink-0">
                        {s.source === 'fallback' && (
                          <span className="rounded bg-rose-50 px-1 py-0.5 text-[10px] text-rose-600" title="Computed from the 0-100 fallback value, not raw components">fallback</span>
                        )}
                        {s.source === 'component' && (
                          <span className="rounded bg-emerald-50 px-1 py-0.5 text-[10px] text-emerald-600" title="Computed from genuine raw components">raw</span>
                        )}
                        <span className="text-sm font-semibold text-cyan-700">
                          {s.result != null ? Number(s.result).toFixed(1) : '—'}
                        </span>
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap gap-1.5">
                      {Object.entries(s.inputs_used).map(([k, v]) => (
                        <span key={k} className="inline-flex items-center gap-1 rounded bg-slate-50 px-1.5 py-0.5 text-[11px] text-slate-600">
                          <Database className="h-3 w-3 text-slate-400" />{k}: <b className="text-slate-700">{v ?? '—'}</b>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-end gap-1 text-xs text-slate-400">
                  mean of sub-results <ArrowRight className="h-3 w-3" />
                  <b className="text-slate-600">{data.index != null ? Number(data.index).toFixed(1) : '--'}</b>
                </div>
              </div>
            )}

            {/* Cohort breakdown */}
            {data.cohort_breakdown?.length > 1 && (
              <div className="space-y-1.5">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Per-cohort ({data.period})</div>
                {data.cohort_breakdown.map((c) => (
                  <div key={c.cohort_id} className="flex items-center justify-between text-xs" data-testid={`audit-cohort-${c.cohort_id}`}>
                    <span className={cn('text-slate-600', c.cohort_id === 'all' && 'font-semibold text-slate-800')}>
                      {c.cohort_id === 'all' ? 'Org roll-up' : c.cohort_id.replace(/^cohort_/, '').replace(/_/g, ' ')}
                    </span>
                    <span className="font-medium text-slate-700">{c.index != null ? Number(c.index).toFixed(1) : '--'}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Source line */}
            {data.raw_available && (
              <div className="text-[11px] text-slate-400 border-t pt-2">
                Source: {data.source_system || 'manual'} · period {data.period}
                {data.updated_at ? ` · updated ${String(data.updated_at).slice(0, 10)}` : ''}
              </div>
            )}
          </div>
        )}

        {!loading && data?.error && (
          <div className="mt-6 text-sm text-rose-600">Could not load the audit trail.</div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default MeasureAuditDrawer;
